import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { ColumnType, TableSettingsComponent } from '../../../shared/components/table-settings/table-settings.component';
import { JetNetInformationComponent } from '../../../shared/components/jetnet-information/jetnet-information.component';
import { AppDialogWrapperService } from '../../../ni-components/app-dialog-wrapper/app-dialog-wrapper.service';


import { AicraftHelper } from '../../../../utils/aircraft';
import { AIRCRAFT_IMAGES } from '../../flight-watch/flight-watch-map/aircraft-images';
import { Router } from '@angular/router';

// Services
import { SharedService } from '../../../layouts/shared-service';
import { AirportWatchService } from '../../../services/airportwatch.service';
import { CustomerinfobygroupService } from '../../../services/customerinfobygroup.service';
import { FbosService } from '../../../services/fbos.service';

//Models
import { CustomersListType } from '../../../models/customer';
import {
    ArrivalsDeparturesQueryRequest,
    ArrivalsDeparturesVisitCounts,
    FlightWatchHistorical,
} from '../../../models/flight-watch-historical';
import { AirportWatchHistoricalParking } from '../../../models/airport-watch-historical-parking';
import {
    AircraftAssignModalComponent,
    NewCustomerAircraftDialogData,
    AircraftAssignResult,
} from '../../../shared/components/aircraft-assign-modal/aircraft-assign-modal.component';
import { csvFileOptions, GridBase } from '../../../services/tables/GridBase';
import { localStorageAccessConstant } from '../../../constants/LocalStorageAccessConstant';
import { SelectedDateFilter } from '../../../shared/components/preset-date-filter/preset-date-filter.component';
import { FbosGridViewModel } from '../../../models/FbosGridViewModel';
import { debounceTime } from 'rxjs/operators';

@Component({
    selector: 'app-analytics-airport-arrivals-depatures',
    styleUrls: ['./analytics-airport-arrivals-depatures.component.scss'],
    templateUrl: './analytics-airport-arrivals-depatures.component.html',
})
export class AnalyticsAirportArrivalsDepaturesComponent
    extends GridBase
    implements OnInit
{
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    @Input() customers: CustomersListType[] = [];
    @Output() refreshCustomers = new EventEmitter();

    chartName = 'airport-arrivals-depatures-table';

    icao: string;
    selectedDateFilter: SelectedDateFilter;

    hideCommercialAicrafts = false;

    data: FlightWatchHistorical[];

    fbo: any;
    allFbos: FbosGridViewModel[] = [];
    selectFbos: FbosGridViewModel[] = [];

    filtersChanged: Subject<any> = new Subject<any>();

    aircraftTypes = AIRCRAFT_IMAGES;

    tableLocalStorageKey: string;

    columns: ColumnType[] = [];

    fboName: string = '';

    tailNumbers: any[] = [];

    hiddenColumns: string[] = ['hexCode', 'flightNumber', 'pastVisits', 'visitsToMyFbo', 'isConfirmedVisit', 'percentOfVisits'];

    initialColumns: ColumnType[] = [
        {
            id: 'company',
            name: 'Company',
        },
        {
            id: 'tailNumber',
            name: 'Tail #',
        },
        {
            id: 'flightNumber',
            name: 'Flight #',
        },
        {
            id: 'hexCode',
            name: 'Hex #',
            hidden: true,
        },
        {
            id: 'aircraftType',
            name: `Aircraft`,
        },
        {
            id: 'dateTime',
            name: 'Date and Time',
            sort: 'desc',
        },
        {
            id: 'status',
            name: 'Departure / Arrival',
        },
        {
            id: 'pastVisits',
            name: 'Past Visits',
        },
        {
            id: 'visitsToMyFbo',
            name: 'Visits to My FBO',
        },
        {
            id: 'isConfirmedVisit',
            name: 'Visit to My FBO?',
        },
        {
            id: 'percentOfVisits',
            name: 'Percent of Visits',
        },
        {
            id: 'originated',
            name: 'Origin ICAO',
        },
        {
            id: 'customerActionStatus',
            name:'Customer Action Status'
        }
    ];

    csvFileOptions: csvFileOptions = {
        fileName: 'Airport Departures and Arrivals',
        sheetName: 'Airport Departures and Arrivals',
    };

    filtersChangeSubscription: Subscription;
    sortChangeSubscription: Subscription;
    needsAttentionOptions: any[];
    statusOptions: any[];

    //Progressive loading over the paged arrivals-departures endpoint: rows stream in page by
    //page so any range (a year included) loads without one giant request.
    isProgressiveLoading = false;
    loadedCount = 0;
    progressTotalCount = 0;
    hasLoadGaps = false;
    private loadGeneration = 0;
    //Per-aircraft PastVisits / VisitsToMyFbo for the current load, keyed "hex|tail" (the
    //server's grouping). Fetched once per load and merged into every page.
    private visitCountsByAircraft = new Map<string, ArrivalsDeparturesVisitCounts>();
    private static readonly queryPageSize = 500;
    //Each in-flight page runs the windowed SQL query; keep concurrency modest to avoid
    //hammering the database.
    private static readonly maxConcurrentPageRequests = 3;
    //Above this row count the client-side xlsx export freezes the tab; stream the CSV from the
    //server instead.
    private static readonly serverExportRowThreshold = 20000;

    constructor(
        private airportWatchService: AirportWatchService,
        private sharedService: SharedService,
        private ngxLoader: NgxUiLoaderService,
        private customerInfoByGroupService: CustomerinfobygroupService,
        private fbosService: FbosService,
        private appDialogWrapperService: AppDialogWrapperService,
        private router: Router,
    ) {
        super();
        this.icao = this.sharedService.getCurrentUserPropertyValue(
            localStorageAccessConstant.icao
        );
        this.filtersChangeSubscription = this.filtersChanged
            .pipe(debounceTime(500))
            .subscribe(() => this.refreshDataSource());
        this.initColumns();
    }

    get visibleColumns() {
        return (
            this.columns
                .filter((column) => !column.hidden)
                .map((column) => column.id) || []
        );
    }

    get isJetNetIntegrationEnabled() {
        return this.sharedService.currentUser.isJetNetIntegrationEnabled;
    }

    get loadProgressPercent(): number {
        return this.progressTotalCount > 0
            ? Math.min(100, Math.round((this.loadedCount / this.progressTotalCount) * 100))
            : 0;
    }

    //True when the user is searching/filtering the grid. Used to warn that results are partial
    //while pages are still streaming in - an empty search result mid-load must read as "wait",
    //not "this customer never visited".
    get hasActiveGridFilter(): boolean {
        const columnFilterActive = (this.dataSource?.filterCollection ?? []).some(
            (f: any) => f?.isFiltered
        );
        if (columnFilterActive) {
            return true;
        }

        //The global search stores its state as a JSON array in dataSource.filter, which can be
        //present with an empty filterValue - only a non-empty value counts as searching.
        const rawFilter = this.dataSource?.filter;
        if (!rawFilter) {
            return false;
        }
        try {
            const parsed = JSON.parse(rawFilter);
            return Array.isArray(parsed)
                ? parsed.some((f: any) => (f?.filterValue ?? '').toString().trim() !== '')
                : rawFilter.toString().trim() !== '';
        } catch {
            return rawFilter.toString().trim() !== '';
        }
    }

    ngOnInit() {
        this.fbosService
        .getFbosByIcao(this.icao)
        .subscribe((data: FbosGridViewModel[]) => {
            this.allFbos = data.filter((fbo) => fbo.acukwikFboHandlerId != null);
            let currentFbo = this.allFbos.find((fbo) =>  fbo.oid == this.sharedService.currentUser.fboId);
            this.selectFbos.push(currentFbo);
            let otherOpt = { ...currentFbo };
            otherOpt.oid = 0;
            otherOpt.acukwikFboHandlerId = 0;
            otherOpt.fbo = 'Other';
            this.selectFbos.push(otherOpt);
            this.fbo = currentFbo;
            this.fboName = currentFbo.fbo;
        });
        this.getCustomersList();
        this.sortChangeSubscription = this.sort.sortChange.subscribe(() => {
            this.columns = this.columns.map((column) =>
                column.id === this.sort.active
                    ? { ...column, sort: this.sort.direction }
                    : {
                          hidden: column.hidden,
                          id: column.id,
                          name: column.name,
                      }
            );

            this.saveSettings();
        });

        var needsAttentionOptionsList = ['Setup Required', 'Email Required', 'Top Customer', 'New Customer']
        this.needsAttentionOptions = needsAttentionOptionsList.map((nl) => ({
            label: nl,
            value: nl,
        }));

        var statusOptionsList = ['Arrival', 'Departure'];
        this.statusOptions = statusOptionsList.map((s) => ({
            label: s,
            value: s,
        }));

        this.refreshData();
    }

    getCustomersList() {
        if (this.customers && this.customers.length > 0) return;
        this.customerInfoByGroupService
            .getCustomersListByGroupAndFbo(
                this.sharedService.currentUser.groupId,
                this.sharedService.currentUser.fboId
            )
            .subscribe((customers: any[]) => {
                this.customers = customers;
            });
    }

    fetchSwimData(startDate: Date, endDate: Date, icao: string) {
        return this.airportWatchService.getArrivalsDeparturesSwim(
            this.sharedService.currentUser.fboId,
            {
                endDateTime: endDate,
                startDateTime: startDate,
            },
            this.icao
        );
    }

    initColumns() {
        this.tableLocalStorageKey = `analytics-airport-arrivals-depatures-${this.sharedService.currentUser.fboId}`;
        this.columns = this.getClientSavedColumns(
            this.tableLocalStorageKey,
            this.initialColumns
        );
    }
    ngOnDestroy() {
        this.loadGeneration++;
        this.sortChangeSubscription?.unsubscribe();
        this.filtersChangeSubscription?.unsubscribe();
    }
    refreshData(isLoaderActive: boolean = false) {
        let endDate = this.getEndOfDayTime(this.filterEndDate);
        let startDate = this.getStartOfDayTime(this.filterStartDate);

        if (!isLoaderActive)
            this.ngxLoader.startLoader(this.chartName);

        if (this.sharedService.currentUser.icao == this.icao) {
            this.loadPagedData(startDate, endDate);
        }
        else {
            this.loadGeneration++;
            this.isProgressiveLoading = false;
            this.fetchSwimData(startDate, endDate, this.icao).subscribe(
                (data: FlightWatchHistorical[]) => {
                    this.data = data;
                    this.refreshDataSource();
                },
                () => { },
                () => {
                    this.ngxLoader.stopLoader(this.chartName);
                }
            );
        }
    }

    private async loadPagedData(startDate: Date, endDate: Date): Promise<void> {
        //A new load (changed range/icao, destroy) bumps the generation; in-flight pages from
        //older loads notice and abandon their results.
        const generation = ++this.loadGeneration;
        const groupId = this.sharedService.currentUser.groupId;
        const fboId = this.sharedService.currentUser.fboId;
        const baseRequest: ArrivalsDeparturesQueryRequest = {
            icao: this.icao,
            startDateTime: startDate,
            endDateTime: endDate,
            page: 1,
            pageSize: AnalyticsAirportArrivalsDepaturesComponent.queryPageSize,
            sortBy: 'dateTime',
            sortDirection: 'desc',
        };

        this.isProgressiveLoading = true;
        this.loadedCount = 0;
        this.progressTotalCount = 0;
        this.hasLoadGaps = false;

        const fetchPage = (page: number) =>
            firstValueFrom(
                this.airportWatchService.queryArrivalsDepartures(groupId, fboId, {
                    ...baseRequest,
                    page,
                })
            );

        //The page rows do not carry the visit counts; one aggregate call per load does, fetched
        //alongside page 1. Nothing is cached server-side, so a "Visit to my FBO?" toggle shows
        //on the next load no matter which web instance serves it.
        const fetchVisitCounts = () =>
            firstValueFrom(
                this.airportWatchService.getArrivalsDeparturesVisitCounts(groupId, fboId, baseRequest)
            );

        try {
            const [firstPage, visitCounts] = await Promise.all([fetchPage(1), fetchVisitCounts()]);
            if (generation !== this.loadGeneration) return;

            this.visitCountsByAircraft = new Map(
                visitCounts.map((c) => [this.aircraftKey(c.aircraftHexCode, c.tailNumber), c])
            );
            this.data = this.applyVisitCounts(firstPage.items);
            this.loadedCount = this.data.length;
            this.progressTotalCount = firstPage.totalCount;
            //First page renders immediately; the rest streams in behind it.
            this.refreshDataSource();
            this.ngxLoader.stopLoader(this.chartName);

            const totalPages = Math.ceil(firstPage.totalCount / baseRequest.pageSize);
            let nextPage = 2;
            let lastGridRefresh = Date.now();
            let failedPages = 0;

            const fetchWorker = async () => {
                while (true) {
                    if (generation !== this.loadGeneration) return;
                    const page = nextPage++;
                    if (page > totalPages) return;

                    //One retry per page; a page that fails twice is recorded so the user sees a
                    //partial-load warning instead of silently missing rows.
                    let result = null;
                    try {
                        result = await fetchPage(page);
                    } catch {
                        try {
                            result = await fetchPage(page);
                        } catch {
                            failedPages++;
                            continue;
                        }
                    }
                    if (generation !== this.loadGeneration) return;

                    this.data = this.data.concat(this.applyVisitCounts(result.items));
                    this.loadedCount = this.data.length;

                    //A grid refresh re-filters, re-sorts and re-renders everything loaded so far
                    //(a growing array, ultimately the full range), so during the stream it runs
                    //on a clock - refreshing per page saturated the main thread and made hover
                    //and scrolling stutter. The count/progress bar still ticks per page.
                    if (Date.now() - lastGridRefresh >= 5000) {
                        lastGridRefresh = Date.now();
                        this.refreshDataSource();
                    }
                }
            };

            const workerCount = Math.min(
                AnalyticsAirportArrivalsDepaturesComponent.maxConcurrentPageRequests,
                Math.max(totalPages - 1, 0)
            );
            await Promise.all(Array.from({ length: workerCount }, () => fetchWorker()));
            if (generation !== this.loadGeneration) return;

            this.isProgressiveLoading = false;
            this.hasLoadGaps = failedPages > 0;
            this.refreshDataSource();
        } catch {
            if (generation !== this.loadGeneration) return;
            this.isProgressiveLoading = false;
            this.hasLoadGaps = this.progressTotalCount > 0 && this.loadedCount < this.progressTotalCount;
            this.ngxLoader.stopLoader(this.chartName);
        }
    }

    private aircraftKey(hexCode: string, tailNumber: string): string {
        return `${hexCode}|${tailNumber}`;
    }

    //Aircraft missing from the counts (a departure whose landing fell outside the range) read as
    //0 visits, as the server used to fill them.
    private applyVisitCounts(rows: FlightWatchHistorical[]): FlightWatchHistorical[] {
        for (const row of rows) {
            const counts = this.visitCountsByAircraft.get(this.aircraftKey(row.hexCode, row.tailNumber));
            row.pastVisits = counts?.pastVisits ?? 0;
            row.visitsToMyFbo = counts?.visitsToMyFbo ?? 0;
            row.percentOfVisits =
                row.visitsToMyFbo > 0 && row.pastVisits > 0 ? row.visitsToMyFbo / row.pastVisits : 0;
        }
        return rows;
    }

    refreshDataSource() {
        this.ngxLoader.startLoader(this.chartName);
        const data = this.data.filter(
            (x) => { return (!this.hideCommercialAicrafts)? true:  !AicraftHelper.isCommercialAircraft(x.flightNumber,x.company,x.aircraftTypeCode)}
        ).map((x) => ({
            ...x,
            aircraftTypeCode: this.getAircraftLabel(x.aircraftTypeCode),
            isParkedWithinGeofence: x.parkingAcukwikFBOHandlerId == this.fbo.acukwikFboHandlerId
        }));

        this.setVirtualScrollVariables(this.paginator, this.sort, data);

        if (!this.dataSource) {
            this.dataSource.filteredData = [];
        }

        //Single pass instead of Set + per-tail .find(): the old shape was O(tails * rows) and
        //locks the tab up at year-scale row counts. Same semantics: tails that pass the
        //commercial filter, each represented by its first occurrence in the unfiltered data.
        const allowedTails = new Set<string>();
        for (const x of this.data) {
            if (!this.hideCommercialAicrafts ||
                !AicraftHelper.isCommercialAircraft(x.flightNumber, x.company, x.aircraftTypeCode) ||
                !AicraftHelper.isCommercialAircraft(x.tailNumber, x.company, x.aircraftTypeCode)) {
                allowedTails.add(x.tailNumber);
            }
        }
        const firstOccurrenceByTail = new Map<string, FlightWatchHistorical>();
        for (const x of this.data) {
            if (allowedTails.has(x.tailNumber) && !firstOccurrenceByTail.has(x.tailNumber)) {
                firstOccurrenceByTail.set(x.tailNumber, x);
            }
        }
        this.tailNumbers = [...firstOccurrenceByTail.values()];
        this.ngxLoader.stopAllLoader(this.chartName);
    }

    filterChanged(value: any = null) {
        if(typeof value == "boolean")
            this.hideCommercialAicrafts = value;

        this.filtersChanged.next({});
    }

    onClickAircraft(row: FlightWatchHistorical) {
        if (!row.company) {
            const dialogWrapper = this.appDialogWrapperService.open<AircraftAssignResult>({
                title: 'Add Aircraft To Customer',
                component: AircraftAssignModalComponent,
                data: {
                    customers: this.customers,
                    tailNumber: row.flightNumber == null ? row.tailNumber : row.flightNumber,
                } as NewCustomerAircraftDialogData,
                width: '450px',
            });

            dialogWrapper.afterClosed().subscribe((result: AircraftAssignResult) => {
                if (!result) {
                    return; // User cancelled
                }
                this.refreshData();
                this.refreshCustomers.emit();
            });
        }
    }

    goToCustomerDetails(row: FlightWatchHistorical) {
        if (row.customerInfoByGroupID > 0) {
            this.router.navigate(['/default-layout/customers', row.customerInfoByGroupID]);
        }
    }

    exportCsv() {
        //xlsx generation on the client freezes the tab well below year-scale row counts; big
        //datasets stream from the server as CSV instead (full requested range, server-side
        //columns - client column filters are not applied there).
        if (
            this.sharedService.currentUser.icao == this.icao &&
            (this.dataSource?.filteredData?.length ?? 0) >
                AnalyticsAirportArrivalsDepaturesComponent.serverExportRowThreshold
        ) {
            this.exportCsvFromServer();
            return;
        }

        let computePropertyFnc = (item: any[], id: string): any => {
            if('isConfirmedVisit' == id)
                item[id] = item[id] ? 'Yes' : 'No';
            if(id == 'dateTime')
                item[id] = this.getlocalDateTime(item[id]);
            else
            if (id == 'aircraftTypeCode')
                item[id] = this.getAircraftLabel(item[id]);
            else if (id == "customerActionStatus") {
                return this.getNeedsAttentionDisplayString(item);
            }
            else return null;
        };
        this.exportCsvFile(
            this.columns,
            this.csvFileOptions.fileName,
            this.csvFileOptions.sheetName,
            computePropertyFnc
        );
    }

    private exportCsvFromServer() {
        const body: ArrivalsDeparturesQueryRequest = {
            icao: this.icao,
            startDateTime: this.getStartOfDayTime(this.filterStartDate),
            endDateTime: this.getEndOfDayTime(this.filterEndDate),
            page: 1,
            pageSize: AnalyticsAirportArrivalsDepaturesComponent.queryPageSize,
        };
        this.ngxLoader.startLoader(this.chartName);
        this.airportWatchService
            .exportArrivalsDepartures(
                this.sharedService.currentUser.groupId,
                this.sharedService.currentUser.fboId,
                body
            )
            .subscribe(
                (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${this.csvFileOptions.fileName}.csv`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                },
                () => { },
                () => {
                    this.ngxLoader.stopLoader(this.chartName);
                }
            );
    }

    clearAllFilters() {
        this.hideCommercialAicrafts = false;

        this.dataSource.filter = '';
        for (const filter of this.dataSource.filterCollection) {
            if (filter.isGlobal) {
                continue;
            }

            filter.dateFilter = {
                endDate: null,
                startDate: null,
            };
            filter.stringFilter = '';
            filter.numberRangeFilter = {
                end: null,
                start: null,
            };
            filter.isFiltered = false;
        }

        this.filterChanged();
    }

    //Row identity for mat-table reuse: without trackBy, every grid refresh during progressive
    //loading recreates all visible row elements, dropping hover state and burning render time.
    //Swim rows have no historical-data id, so those fall back to a composite key.
    trackByRow = (_index: number, item: FlightWatchHistorical) =>
        item.airportWatchHistoricalDataId ||
        `${item.tailNumber}|${item.dateTime}|${item.status}`;

    private aircraftLabelCache = new Map<string, string>();

    getAircraftLabel(type: string) {
        //Memoized: this runs once per row on every grid refresh, and the linear scan over the
        //aircraft-image list added up to millions of comparisons per refresh at year-scale.
        let label = this.aircraftLabelCache.get(type);
        if (label === undefined) {
            const found = this.aircraftTypes.find((a) => a.id === type);
            label = found ? found.label : 'Other';
            this.aircraftLabelCache.set(type, label);
        }
        return label;
    }

    getNeedsAttentionDisplayString(customer: any): any {
        let message = customer.customerNeedsAttention;
        return message;
    }

    openSettings() {
        const dialogWrapper = this.appDialogWrapperService.open<ColumnType[]>({
            title: 'Table Settings',
            component: TableSettingsComponent,
            data: [...this.columns],
            width: '600px'
        });

        dialogWrapper.afterClosed().subscribe((result: ColumnType[]) => {
            if (!result) {
                return; // User cancelled
            }
            this.columns = [...result];
            this.refreshSort(this.sort, this.columns);
            this.saveSettings();
        });
    }

    saveSettings() {
        localStorage.setItem(
            this.tableLocalStorageKey,
            JSON.stringify(this.columns)
        );
    }
    confirmedVisitToggled(row: FlightWatchHistorical): void{
        const previousParking = row.airportWatchHistoricalParking
            ? { ...row.airportWatchHistoricalParking }
            : null;
        if (row.airportWatchHistoricalParking == null) {
            row.airportWatchHistoricalParking = {
                airportWatchHistoricalDataId: row.airportWatchHistoricalDataId,
                acukwikFbohandlerId: 0,
                oid: 0,
            };
        }

        row.airportWatchHistoricalParking.acukwikFbohandlerId = (row.isParkedWithinGeofence)?this.fbo.acukwikFboHandlerId: 0;

        row.airportWatchHistoricalParking.isConfirmed = true;

        this.ngxLoader.startLoader(this.chartName);
        //Save, then patch the affected rows in place instead of reloading the whole table, which
        //re-fetched every page after each toggle.
        const save$ = row.airportWatchHistoricalParking.oid > 0
            ? this.airportWatchService.updateHistoricalParking(row)
            : this.airportWatchService.createHistoricalParking(row);
        save$.subscribe(
            (response: any) => {
                //Create returns the new parking event (with the saved record's id); update returns
                //nothing, so the row's own record - already carrying the new attribution - stands.
                const savedParking: AirportWatchHistoricalParking =
                    response?.airportWatchHistoricalParking ?? row.airportWatchHistoricalParking;
                this.applyConfirmedVisitChange(row, savedParking, row.isParkedWithinGeofence ? 1 : -1);
                this.ngxLoader.stopLoader(this.chartName);
            },
            () => {
                //Save failed: put the toggle back where it was.
                row.airportWatchHistoricalParking = previousParking;
                row.isParkedWithinGeofence = !row.isParkedWithinGeofence;
                this.ngxLoader.stopLoader(this.chartName);
            }
        );
    }

    //After a toggle is saved: stamp the new parking attribution on the toggled row and move the
    //"Visits to my FBO" / percent columns of every row for the same aircraft (hex + tail, the
    //server's grouping) by one. Both the source array and the rendered copies are patched - the
    //grid renders mapped copies of this.data, and a later filter/sort refresh rebuilds from the
    //source, so the two must agree.
    private applyConfirmedVisitChange(
        row: FlightWatchHistorical,
        parking: AirportWatchHistoricalParking,
        visitDelta: number
    ): void {
        const isMyFbo = parking.acukwikFbohandlerId == this.fbo.acukwikFboHandlerId;
        //Pages still streaming in for this load merge from the counts map, so move it too.
        const key = this.aircraftKey(row.hexCode, row.tailNumber);
        const counts = this.visitCountsByAircraft.get(key) ?? {
            aircraftHexCode: row.hexCode,
            tailNumber: row.tailNumber,
            pastVisits: row.pastVisits ?? 0,
            visitsToMyFbo: 0,
        };
        counts.visitsToMyFbo = Math.max(0, counts.visitsToMyFbo + visitDelta);
        this.visitCountsByAircraft.set(key, counts);
        for (const item of [...this.data, ...(this.dataSource?.data ?? [])]) {
            if (item.airportWatchHistoricalDataId == row.airportWatchHistoricalDataId) {
                item.airportWatchHistoricalParking = parking;
                item.parkingAcukwikFBOHandlerId = parking.acukwikFbohandlerId;
                item.isParkedWithinGeofence = isMyFbo;
            }
            if (item.hexCode == row.hexCode && item.tailNumber == row.tailNumber) {
                item.visitsToMyFbo = Math.max(0, (item.visitsToMyFbo ?? 0) + visitDelta);
                item.percentOfVisits =
                    item.visitsToMyFbo > 0 && item.pastVisits > 0
                        ? item.visitsToMyFbo / item.pastVisits
                        : 0;
            }
        }
    }
    changeIcaoFilter($event: string) {
        this.icao = $event;
        this.setColumns();
        this.refreshData();
    }
    applyPresetDateFilter(filter: SelectedDateFilter) {
        this.filterEndDate = filter.limitDate;
        this.filterStartDate = filter.offsetDate;
        this.refreshData();
    }
    tailNumberSearch(tailNumber: any) {
        if (tailNumber.trim() != "") {
            const dialogWrapper = this.appDialogWrapperService.open<number>({
                title: 'JetNet Information',
                component: JetNetInformationComponent,
                data: tailNumber.trim(),
                width: '1100px'
            });
            dialogWrapper
                .afterClosed()
                .subscribe((result: number) => {
                    if (result != null && result > 0) {
                        this.refreshData();
                    }
                });
        }
    }
    getlocalDateTime(date: Date): string{
        let curentdate = new Date(date+"Z");
        let localdate = curentdate.toLocaleString();
        return localdate;
    }
    private setColumns() {
        this.columns = this.filteredColumns;
    }
    get filteredColumns() {
        var filteredColumns = this.initialColumns;
        //if (!filteredColumns.find((column) => column.id === 'originated')) {
        //    filteredColumns.push({
        //        id: 'originated',
        //        name: 'Origin ICAO',
        //    });
        //};

        if (this.icao != this.sharedService.currentUser.icao)
            return filteredColumns.filter((column) => {
                return !this.hiddenColumns.includes(column.id);
            });
        else
            return filteredColumns;
    }
}
